import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";

interface OrganizationInfo {
  id: string;
  name: string;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

interface UserInfo {
  id: string;
  email: string;
  createdAt: string;
  hasConnectedEmail: boolean;
  organizationName?: string;
}

export function AdminOrganizationsPage() {
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Form states
  const [newOrgName, setNewOrgName] = useState("");
  const [editOrgName, setEditOrgName] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<OrganizationInfo | null>(null);
  const [orgUsers, setOrgUsers] = useState<UserInfo[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ organizations: OrganizationInfo[] }>(
        `/admin/organizations/list`,
        {}
      );
      setOrganizations(res.organizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      setError(null);
      const res = await apiClient.post<{ organization: OrganizationInfo }>(
        `/admin/organizations/create`,
        { name: newOrgName.trim() }
      );
      setOrganizations([res.organization, ...organizations]);
      setCreateDialogOpen(false);
      setNewOrgName("");
    } catch (error: any) {
      console.error("Error creating organization:", error);
      setError(error.message || "Failed to create organization");
    }
  };

  const handleUpdateOrganization = async () => {
    if (!selectedOrg) return;
    
    try {
      setError(null);
      const res = await apiClient.put<{ organization: OrganizationInfo }>(
        `/admin/organizations/update`,
        { id: selectedOrg.id, name: editOrgName.trim() }
      );
      setOrganizations(organizations.map(org => 
        org.id === selectedOrg.id ? res.organization : org
      ));
      setEditDialogOpen(false);
      setEditOrgName("");
      setSelectedOrg(null);
    } catch (error: any) {
      console.error("Error updating organization:", error);
      setError(error.message || "Failed to update organization");
    }
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrg) return;
    
    try {
      setError(null);
      await apiClient.delete(`/admin/organizations/delete`, {
        id: selectedOrg.id,
      });
      setOrganizations(organizations.filter(org => org.id !== selectedOrg.id));
      setDeleteConfirmOpen(false);
      setSelectedOrg(null);
    } catch (error: any) {
      console.error("Error deleting organization:", error);
      setError(error.message || "Failed to delete organization");
    }
  };

  const handleViewUsers = async (org: OrganizationInfo) => {
    try {
      setLoadingUsers(true);
      setError(null);
      setSelectedOrg(org);
      const res = await apiClient.post<{ users: UserInfo[] }>(
        `/admin/organizations/users`,
        { organizationId: org.id }
      );
      setOrgUsers(res.users);
      setUsersDialogOpen(true);
    } catch (error) {
      console.error("Error fetching organization users:", error);
      setError("Failed to load organization users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const openEditDialog = (org: OrganizationInfo) => {
    setSelectedOrg(org);
    setEditOrgName(org.name);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (org: OrganizationInfo) => {
    setSelectedOrg(org);
    setDeleteConfirmOpen(true);
  };

  if (loading) {
    return (
      <AdminPage>
        <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Organization Management
          </Typography>
          <Button
            variant="outlined"
            startIcon={<StorageIcon />}
            onClick={() => navigate("/admin/extracted-data")}
            sx={{ mr: 2 }}
          >
            Extracted Data
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Organization
          </Button>
        </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: "16px" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell align="center"><strong>Users</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Updated</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No organizations found
                </TableCell>
              </TableRow>
            ) : (
              organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{org.name}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={org.userCount} 
                      size="small" 
                      color={org.userCount > 0 ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(org.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(org.updatedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewUsers(org)}
                      title="View users"
                    >
                      <PeopleIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => openEditDialog(org)}
                      title="Edit organization"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => openDeleteDialog(org)}
                      disabled={org.userCount > 0}
                      title={org.userCount > 0 ? "Cannot delete organization with users" : "Delete organization"}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && newOrgName.trim()) {
                handleCreateOrganization();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateOrganization} 
            variant="contained"
            disabled={!newOrgName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editOrgName}
            onChange={(e) => setEditOrgName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && editOrgName.trim()) {
                handleUpdateOrganization();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateOrganization} 
            variant="contained"
            disabled={!editOrgName.trim()}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the organization "{selectedOrg?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteOrganization} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Dialog */}
      <Dialog 
        open={usersDialogOpen} 
        onClose={() => setUsersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Users in {selectedOrg?.name}
        </DialogTitle>
        <DialogContent>
          {loadingUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress />
            </Box>
          ) : orgUsers.length === 0 ? (
            <Typography align="center" sx={{ padding: "20px" }}>
              No users in this organization
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Connected Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{user.email}</TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{user.hasConnectedEmail ? "✓" : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </AdminPage>
  );
}